import React, { useState, useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import { VatSelector, Button } from "@components";
import { safeParseFloat } from "../../utils/parseNumber";
import { formFooter } from "@design-system";

// Interface for form data
interface IKilometerPriceFormData {
  price: number | string; // Allow string for input flexibility
  vat: number;
}

// Interface for component props
export interface KilometerPriceFormProps {
  initialPrice?: number;
  initialVat?: number;
  onConfirm: (data: { price: number; vat: number }) => void;
  onCancel: () => void;
}

export const KilometerPriceForm: FC<KilometerPriceFormProps> = ({
  initialPrice = 0, // Keep default prop value as 0 for logic
  initialVat = 25,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation(["common"]);

  // Single state object for actual form data
  const [formData, setFormData] = useState<IKilometerPriceFormData>({
    // Set price to '' if initialPrice is 0 or undefined, otherwise use initialPrice
    price: initialPrice > 0 ? initialPrice : "",
    vat: initialVat,
  });

  // Effect to reset form when initial props change (e.g., modal reopens)
  useEffect(() => {
    // Set price to '' if initialPrice is 0, otherwise use initialPrice
    setFormData({
      price: initialPrice > 0 ? initialPrice : "",
      vat: initialVat,
    });
  }, [initialPrice, initialVat]);

  // --- Input Handlers ---

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid non-negative numbers
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setFormData((prevData) => ({ ...prevData, price: value }));
    }
  };

  const handleVatChange = (value: number) => {
    setFormData((prevData) => ({ ...prevData, vat: value }));
  };

  // --- Form Submission ---

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission

    // Validate and format data before confirming
    const finalPrice = safeParseFloat(formData.price); // Ensure price is a number
    const finalVat = formData.vat;

    // Simple validation example: price must be non-negative
    if (finalPrice < 0) {
      console.error("Price cannot be negative."); // Replace with user-friendly validation feedback
      // Optionally show a validation message to the user here
      return;
    }
    if (finalVat < 0 || finalVat > 100) {
      console.error("VAT must be between 0 and 100.");
      return;
    }

    onConfirm({ price: finalPrice, vat: finalVat });
  };

  // --- Render Logic ---

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Description Text */}
      <p className="text-sm text-muted">
        {t(
          "common:kilometer_price_order_level_description",
          "Prisen settes for hele ordren, uavhengig av produktene.",
        )}
      </p>

      {/* Price Input */}
      <div>
        <label
          htmlFor="kilometerPrice"
          className="block mb-2 text-sm font-medium text-text"
        >
          {t("common:price_per_kilometer", "Pris per kilometer")}
        </label>
        <div className="relative">
          <input
            type="number"
            id="kilometerPrice"
            name="kilometerPrice"
            min="0"
            step="any" // Allow decimals
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-lgb-on-dark-background dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            placeholder={t("common:price_per_km", "Price per km")}
            value={formData.price} // Controlled component
            onChange={handlePriceChange}
            required // Basic HTML validation
          />
        </div>
      </div>

      {/* VAT Section - Now using the VatSelector component */}
      <VatSelector
        initialValue={formData.vat}
        presetValues={[12, 15, 25]}
        onChange={handleVatChange}
        labelText={t("vat_percentage")}
        descriptionText={t("vat_percentage_description")}
      />

      {/* Form Buttons */}
      <div className={formFooter}>
        <Button type="button" color="gray" onClick={onCancel}>
          {t("cancel")}
        </Button>
        <Button type="submit" color="primary">
          {t("save")}
        </Button>
      </div>
    </form>
  );
};
